import type { BaseLayout, RaidScore } from '../sim/raid/bases';
import { validateBase } from '../sim/raid/bases';

/**
 * The player's profile (Phases 13 / 15, DECISIONS D93–D99). Guest-first: kept
 * in this browser (localStorage), mirrored to the server when one is
 * configured. Every read is validated and every storage call is guarded, so a
 * private window or blocked storage just means a fresh, unsaved profile.
 */
export interface MatchRecord {
  readonly at: number;
  readonly mode: string;
  readonly result: 'victory' | 'defeat';
  /** Raid only. */
  readonly stars?: number;
  readonly opponent?: string;
  readonly points: number;
  readonly xp: number;
}

export interface Profile {
  readonly v: 1;
  readonly id: string;
  readonly name: string;
  /** Ranking points (raids), never below 0. */
  readonly points: number;
  readonly xp: number;
  readonly history: readonly MatchRecord[];
  readonly base: BaseLayout | null;
  /** Campaign best per level id (D110): stars 0–3, score. */
  readonly campaign?: Readonly<Record<string, { readonly stars: number; readonly score: number }>>;
  /** Season pass (D112): tiers claimed on each track; premium only when the server granted it. */
  readonly pass?: { readonly claimed: readonly string[] };
  /** Equipped cosmetic skin id (D122), or none. */
  readonly skin?: string;
  /** Friend profile ids (D111). */
  readonly friends?: readonly string[];
  /** Server identity once registered (Phase 13 online). */
  readonly server?: { readonly id: string; readonly token: string };
}

const KEY = 'atf-profile';
export const HISTORY_LIMIT = 30;

/** XP needed for each level: 100, 200, 300 … (level n needs 50·n·(n+1) total). */
export function levelOf(xp: number): { level: number; into: number; next: number } {
  let level = 1;
  let need = 100;
  let left = Math.max(0, Math.floor(xp));
  while (left >= need) {
    left -= need;
    level++;
    need += 100;
  }
  return { level, into: left, next: need };
}

/** XP for a finished match: taking part 20, a win +30, + 10 per kill (max +50), + 15 per raid star. */
export function xpFor(won: boolean, kills: number, stars = 0): number {
  return 20 + (won ? 30 : 0) + Math.min(50, 10 * Math.max(0, kills)) + 15 * stars;
}

export function rankOf(points: number): string {
  return points >= 600 ? 'Warlord' : points >= 300 ? 'Commander' : points >= 120 ? 'Captain' : points >= 40 ? 'Scavenger' : 'Survivor';
}

function newId(): string {
  const a = new Uint8Array(8);
  crypto.getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function freshProfile(): Profile {
  return { v: 1, id: newId(), name: 'Survivor', points: 0, xp: 0, history: [], base: null };
}

/** Accept only a well-formed profile (storage can hold anything). */
export function sanitizeProfile(raw: unknown): Profile | null {
  const p = raw as Profile;
  if (!p || typeof p !== 'object' || p.v !== 1 || typeof p.id !== 'string' || !/^[0-9a-f]{16}$/.test(p.id)) return null;
  const name = typeof p.name === 'string' ? p.name.replace(/[\u0000-\u001f<>]/g, '').slice(0, 20) || 'Survivor' : 'Survivor';
  const num = (n: unknown) => (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0);
  const history = Array.isArray(p.history) ? p.history.filter((h) => h && typeof h.mode === 'string' && (h.result === 'victory' || h.result === 'defeat')).slice(0, HISTORY_LIMIT) : [];
  const base = p.base && validateBase(p.base) === null ? p.base : null;
  const server = p.server && typeof p.server.id === 'string' && typeof p.server.token === 'string' ? p.server : undefined;
  const campaign: Record<string, { stars: number; score: number }> = {};
  if (p.campaign && typeof p.campaign === 'object') {
    for (const [k, v] of Object.entries(p.campaign)) {
      if (/^\d{1,3}$/.test(k) && v && typeof v === 'object') campaign[k] = { stars: Math.min(3, num(v.stars)), score: num(v.score) };
    }
  }
  const claimed = Array.isArray(p.pass?.claimed) ? p.pass!.claimed.filter((c) => typeof c === 'string' && /^(free|premium):\d{1,2}$/.test(c)).slice(0, 200) : [];
  const friends = Array.isArray(p.friends) ? p.friends.filter((f) => typeof f === 'string' && /^[0-9a-f]{16}$/.test(f)).slice(0, 100) : [];
  const skin = typeof p.skin === 'string' && /^[a-z]{2,12}$/.test(p.skin) ? p.skin : undefined;
  return { v: 1, id: p.id, name, points: num(p.points), xp: num(p.xp), history, base, campaign, pass: { claimed }, friends, ...(skin ? { skin } : {}), ...(server ? { server } : {}) };
}

export function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(KEY);
    const p = raw ? sanitizeProfile(JSON.parse(raw)) : null;
    if (p) return p;
  } catch {
    /* blocked or corrupt storage: fresh profile */
  }
  const p = freshProfile();
  saveProfile(p);
  return p;
}

export function saveProfile(p: Profile): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* not persisted (private mode) */
  }
}

/** Record a finished match: history (newest first), XP, ranking points (raids). Returns the new profile. */
export function recordMatch(p: Profile, rec: Omit<MatchRecord, 'at'>, at = Date.now()): Profile {
  const next: Profile = {
    ...p,
    xp: p.xp + rec.xp,
    points: Math.max(0, p.points + rec.points),
    history: [{ ...rec, at }, ...p.history].slice(0, HISTORY_LIMIT),
  };
  saveProfile(next);
  return next;
}

export function raidRecord(score: RaidScore, won: boolean, kills: number, opponent: string): Omit<MatchRecord, 'at'> {
  return { mode: 'raid', result: won ? 'victory' : 'defeat', stars: score.stars, opponent, points: score.attackerPoints, xp: xpFor(won, kills, score.stars) };
}

/** Keep the best result per level (more stars, then a higher score). Returns the new profile. */
export function recordLevel(p: Profile, levelId: number, r: { stars: number; score: number }): Profile {
  const prev = p.campaign?.[String(levelId)];
  const better = !prev || r.stars > prev.stars || (r.stars === prev.stars && r.score > prev.score);
  if (!better) return p;
  const next: Profile = { ...p, campaign: { ...(p.campaign ?? {}), [String(levelId)]: { stars: r.stars, score: r.score } } };
  saveProfile(next);
  return next;
}

